import React, { useState, useEffect } from 'react';
import { useRouteData, useSiteData, Head } from 'react-static';
import LazyLoad from 'react-lazyload';
// template components
import ContentHub from '@nmx/template/dist/componentLibrary/modules/ContentHub';
import InPageLeadForm from '@nmx/template/dist/componentLibrary/modules/InPageLeadForm';
import Page from '@nmx/template/dist/componentLibrary/layout/Page';
import Theme from '@nmx/template/dist/componentLibrary/Theme';
// native components
import AdvisorConnectModule from './components/advisorConnectModule';
import CredentialCalloutGrouping from './components/credentialCalloutGrouping';
import ExperienceCallout from './components/experienceCallout';
import Faq from './components/faq';
import FinancialPlanningSection from './components/financialPlanningSection';
import HeroSection from './components/heroSection';
import InPageLeadFormLazyLoadContainer from '../../components/inPageLeadFormLazyLoadContainer';
import IntroSection from './components/introSection';
import Metadata from './components/metadata';
import ProductCalloutGrouping from './components/productCalloutGrouping';
import QuizCallout from './components/quizCallout';
import UnderstandingProduct from './components/understandingProduct';
// images for prefetch
import highResDesktopImage from './components/heroSection/images/universal-life-insurance-hero.webp';
import highResMobileImage from './components/heroSection/images/universal-life-insurance-hero-mobile.webp';
// dislaimer footnotes
import {
  GlobalMarketIntelligence,
  NerdWalletAllRightsReserved,
} from '../../../utils/disclaimerFootnotes/disclaimerFootnotes';
// other utils
import { getImmediateMatchFeatureFlag } from '../../../utils/getFeatureFlags/getFeatureFlags';

export default () => {
  const { config, dynamicConfig } = useSiteData();
  const { pageProps, contentHubItems } = useRouteData();
  // We don't want to load recaptcha on page load (for performance reasons) so we will load it once the in page lead for is engaged with
  const [loadRecaptcha, setLoadRecaptcha] = useState(false);
  const [immediateMatchFeatureFlag, setImmediateMatchFeatureFlag] = useState(false);

  useEffect(() => {
    getImmediateMatchFeatureFlag(setImmediateMatchFeatureFlag, config);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Page
      {...pageProps} // defined in route.js
      headComponent={Head}
      config={config}
      dynamicConfig={dynamicConfig}
      useReCaptcha={loadRecaptcha}
      disableDisclaimerTopBorder
      breadcrumbsNavigationConfig={[
        {
          pageName: 'Home',
          linkUrl: '/',
          linkAriaLabel: 'Northwestern Mutual Home',
        },
        {
          pageName: 'Insurance',
          linkUrl: '/insurance/',
          linkAriaLabel: 'Northwestern Mutual Insurance',
        },
        {
          pageName: 'Life Insurance',
          linkUrl: '/life-insurance/',
          linkAriaLabel: 'Northwestern Mutual Life Insurance',
        },
        {
          pageName: 'Universal Life Insurance',
          linkUrl: '/life-insurance/universal-life-insurance/',
          linkAriaLabel: 'Northwestern Mutual Universal Life Insurance',
        },
      ]}
      disclaimerFootnotes={[
        <>
          <sup>1</sup>&nbsp;The amount and timing of your payments will directly affect your policy&#39;s cash value as well as your ability to
          maintain coverage in the future. Your policy&#39;s cash value will increase after if you pay more premium in the early years and you choose to make regular payments.
        </>,
        <>
          <sup>2</sup>&nbsp;There are different ways to use your policy&#39;s accumulated value. These different methods have advantages and disadvantages. There may also be tax implications. Whether you take your money in the form of a surrender, withdrawal, or loan, the policy&#39;s value and death benefit will be reduced. Taking money out will also affect dividends paid on the policy, if those are available. Any money withdrawn from the policy, beyond what you paid in cumulative premiums, will be taxable. Loans are not taxable when they are taken, and can have adverse effects if not managed properly. Policy loans and automatic premium loans, including any accrued interest, must be repaid in cash or cash values when the policy terminates, or the insured person dies. Repaying loans from cash values &#40;other than death benefit funds&#41; can trigger a significant tax event, and there may be little, or no cash value left to pay the tax. If loans and accrued interest reach the amount of your cash value, additional cash payments are necessary or the policy will terminate. Policyowners should consult with their tax advisors about the impact of using their policy&#39;s cash value.
        </>,
        <>
          <sup>3</sup>&nbsp;Unless you cancel or stop paying your premiums.
        </>,
        <>
          <sup>4</sup>&nbsp;Subject to policy minimums, age, insurability requirements, limits mandated by tax code, and a minimum cash value to cover policy expenses. Independent of the death benefit.
        </>,
        <>
          <sup>5</sup>&nbsp;All investments carry some level of risk. No investment strategy can guarantee a profit or protect against a loss.
        </>,
        <>
          {NerdWalletAllRightsReserved(6)}
        </>,
        <>
          {GlobalMarketIntelligence(7)}
        </>,
      ]}
      customDisclaimers={[
        <>
          To be used with form numbers &#91;ICC18.UU.CUL.(0919), UU.SPUL.(1219), ICC18.UU.SPUL.P.(1219), ICC18.UU.SUL.(0919)&#93; or state equivalent.
        </>,
      ]}
      stateFilingComplianceNumber={<>90&#8211;2686&#8211;91 (0324)</>}
    >
      <Head>
        {/* prefetch expensive images */}
        <link rel='preload' href={highResMobileImage} as='image' media={`(max-width: ${Theme.breakpoints.values.medium}px)`} />
        <link rel='preload' href={highResDesktopImage} as='image' media={`(min-width: ${Theme.breakpoints.values.medium + 1}px)`} />
      </Head>

      <Metadata
        image={highResDesktopImage}
        pageDescription={pageProps.pageDescription}
        pageTitle={pageProps.pageTitle}
      />

      <HeroSection />

      <IntroSection />

      <UnderstandingProduct />

      {/* QUIZ CALLOUT */}
      <LazyLoad
        offset={200}
        height={300}
        once
        placeholder={
          <section id='quiz-callout-universal-life-insurance'>
            <h2>How much do you know about life insurance&#63;</h2>
          </section>
        }>
        <QuizCallout />
      </LazyLoad>

      <Faq />

      {/* IN-PAGE LEAD FORM SECTION */}
      <InPageLeadFormLazyLoadContainer>
        <InPageLeadForm
          sectionId='in-page-lead' // note: id in-page-lead-form will be on the Form element itself.
          config={config}
          formLocation='universal-life-insurance'
          immediateMatchFeatureFlagEnabled={immediateMatchFeatureFlag}
          onFormEngaged={() => {
            if (!loadRecaptcha) {
              setLoadRecaptcha(true);
            }
          }}
          variant='embedded'
        />
      </InPageLeadFormLazyLoadContainer>

      <ProductCalloutGrouping />

      <FinancialPlanningSection />

      {/* EXPERIENCE CALLOUT */}
      <LazyLoad
        offset={200}
        height={300}
        once
        placeholder={
          <section id='universal-life-insurance-calc-experience-callout'>
            <h2>Life Insurance Calculator</h2>
            <p>How much life insurance is right for you&#63;</p>
          </section>
        }>
        <ExperienceCallout />
      </LazyLoad>

      <CredentialCalloutGrouping />

      <ContentHub
        sectionId='content-hub'
        headingText='Boost your universal life insurance knowledge'
        contentHubItems={contentHubItems} // defined in route.js
        config={config}
      />

      <AdvisorConnectModule />

    </Page>
  );
};
